/**
 * The product shot's workspace: Ostrey Home, a fictional homeware brand
 * choosing between two kettle samples for its autumn range. Every name here
 * is invented for the concepts (workshops, people, records); none is a
 * customer, prospect or demo tenant.
 *
 * The figures reconcile. Landed cost = unit cost + freight. Margin is at RRP
 * excluding 20% VAT: (RRP / 1.2 − landed) / (RRP / 1.2). Per-unit margin is
 * that same difference in pounds; gross margin at minimum order is the
 * per-unit figure times the minimum order. `fixture.test.ts` recomputes
 * every derived number and checks the answer prints them.
 *
 * Dates sit in the week of Thursday 17 September 2026: samples arrived on
 * 2 and 11 September, the question was asked at 14:02 on the 17th.
 */

import type { ProvenanceActor } from "@product/components/app/runs/provenance-avatar-stack";
import type { ComposerSource } from "@product/components/app/sessions/composer-sources-view";
import type { RecordImage } from "@product/components/content/record-image-viewer";
import type { WorkspaceChannel, WorkspaceField, WorkspaceSession } from "@product/lib/mlx-workspace-client";

/* ------------------------------------------------------------------ */
/*  Photographs: The Met Open Access, CC0                               */
/* ------------------------------------------------------------------ */

/**
 * The four photographs, as attached files. Each is a resized WebP of a
 * public-domain photograph from The Metropolitan Museum of Art's Open Access
 * programme (CC0 1.0), served from apps/web/public/api/client/files so the
 * product's own image components load them from the path shape they use in
 * the application. The objects are two Christopher Dresser designs; in the
 * shot they stand in for workshop samples.
 */
export const PHOTO_SOURCES = [
  {
    fileId: "ostrey-smp-0412-front.webp",
    object: "Kettle, Christopher Dresser, ca. 1885 (copper, brass)",
    accession: "1990.123.1",
    objectUrl: "https://www.metmuseum.org/art/collection/search/207823",
    imageUrl: "https://images.metmuseum.org/CRDImages/es/original/DP-13486-036.jpg",
    licence: "CC0 1.0, The Met Open Access",
  },
  {
    fileId: "ostrey-smp-0412-back.webp",
    object: "Kettle, Christopher Dresser, ca. 1885 (copper, brass)",
    accession: "1990.123.1",
    objectUrl: "https://www.metmuseum.org/art/collection/search/207823",
    imageUrl: "https://images.metmuseum.org/CRDImages/es/original/DP-13486-035.jpg",
    licence: "CC0 1.0, The Met Open Access",
  },
  {
    fileId: "ostrey-smp-0419-front.webp",
    object: "Teapot, Christopher Dresser, ca. 1879 (silverplate, ebony)",
    accession: "2021.153.2",
    objectUrl: "https://www.metmuseum.org/art/collection/search/823191",
    imageUrl: "https://images.metmuseum.org/CRDImages/es/original/DP-18258-025.jpg",
    licence: "CC0 1.0, The Met Open Access",
  },
  {
    fileId: "ostrey-smp-0419-back.webp",
    object: "Teapot, Christopher Dresser, ca. 1879 (silverplate, ebony)",
    accession: "2021.153.2",
    objectUrl: "https://www.metmuseum.org/art/collection/search/823191",
    imageUrl: "https://images.metmuseum.org/CRDImages/es/original/DP-18258-026.jpg",
    licence: "CC0 1.0, The Met Open Access",
  },
] as const;

/** The URL the product builds for an attached file. */
export function attachedFileUrl(fileId: string): string {
  return `/api/client/files/${encodeURIComponent(fileId)}`;
}

/* ------------------------------------------------------------------ */
/*  Workspace                                                           */
/* ------------------------------------------------------------------ */

export const ORGANISATION = { name: "Ostrey Home" } as const;

export const USER: ProvenanceActor = {
  label: "Nadia Brooks",
  imageUrl: null,
  initials: "NB",
};

const DAY = 86_400_000;
export const MINUTE = 60_000;
/** 17 September 2026, 14:02 London time. */
export const NOW = Date.UTC(2026, 8, 17, 13, 2);

export const CHANNEL_ID = "ch_autumn_range";
export const CHANNEL_NAME = "Autumn range";
export const DATA_SET_NAME = "Samples";

function session(
  id: string,
  title: string,
  status: string,
  ageMinutes: number,
): WorkspaceSession {
  return {
    id,
    title,
    status,
    createdAt: NOW - ageMinutes * MINUTE,
    updatedAt: NOW - ageMinutes * MINUTE,
    visibility: "private",
  };
}

export const ACTIVE_SESSION_ID = "ses_autumn_kettles";
export const SESSION_TITLE = "Autumn kettle samples";

/**
 * The workspace's connected sources as the composer's sources row lists them
 * (ComposerSourcesView): the connections list's Xero and Google Drive
 * entries, with the plugins' own icons.
 */
export const COMPOSER_SOURCES: readonly ComposerSource[] = [
  { id: "xero", label: "Xero", pluginSlugs: ["xero"], iconUrl: "/concepts/product-shot/icons/sources/xero.svg" },
  {
    id: "google-drive",
    label: "Google Drive",
    pluginSlugs: ["google-drive"],
    iconUrl: "/concepts/product-shot/icons/sources/google-drive.png",
  },
];

/** The session list, newest first; one session is still running. */
export const SESSIONS: WorkspaceSession[] = [
  session(ACTIVE_SESSION_ID, SESSION_TITLE, "completed", 1),
  session("ses_sept_bills", "Reconcile September supplier bills", "running", 6),
  session("ses_qc_failures", "Samples that failed QC", "completed", 40),
  session("ses_late_pos", "Purchase orders due this month", "completed", 95),
  session("ses_glaze_q3", "Glaze defects by workshop, Q3", "completed", 1 * 24 * 60),
  session("ses_spring_margin", "Spring range margin review", "completed", 2 * 24 * 60),
  session("ses_lead_times", "Workshop lead times", "completed", 3 * 24 * 60),
  session("ses_brackwater_invoice", "Brackwater invoice query", "completed", 6 * 24 * 60),
  session("ses_photo_brief", "Photography brief for autumn", "completed", 8 * 24 * 60),
];

/** The session list while the open session's run works. */
export const SESSIONS_RUNNING: WorkspaceSession[] = SESSIONS.map((entry) =>
  entry.id === ACTIVE_SESSION_ID ? { ...entry, status: "running" } : entry,
);

/** Finished since Nadia last opened it. */
export const UNREAD_SESSION_IDS: readonly string[] = ["ses_late_pos"];

/** Pinned rows, as the navigation pins store returns them. */
export const PINNED_SESSIONS: WorkspaceSession[] = [
  session("ses_costings", "Autumn range costings", "completed", 12 * 24 * 60),
];

function channel(id: string, name: string, ageDays: number): WorkspaceChannel {
  return {
    id,
    name,
    status: "active",
    createdAt: NOW - ageDays * DAY,
    updatedAt: NOW - ageDays * DAY,
  };
}

export const CHANNELS: WorkspaceChannel[] = [
  channel(CHANNEL_ID, CHANNEL_NAME, 0),
  channel("ch_workshops", "Workshops", 1),
  channel("ch_purchasing", "Purchasing", 1),
  channel("ch_finance", "Finance", 3),
  channel("ch_photography", "Photography", 9),
];

/* ------------------------------------------------------------------ */
/*  The samples                                                         */
/* ------------------------------------------------------------------ */

export type SampleRecord = {
  id: string;
  code: string;
  title: string;
  workshop: string;
  material: string;
  capacityLitres: number;
  unitCost: number;
  freight: number;
  rrp: number;
  leadTimeWeeks: number;
  minimumOrder: number;
  qc: "Passed" | "Needs fix";
  qcNote: string;
  received: string;
  /** The attached photographs, front then back. */
  photos: readonly { fileId: string }[];
};

export const SAMPLES: readonly SampleRecord[] = [
  {
    id: "rec_smp_0412",
    code: "SMP-0412",
    title: "Copper stovetop kettle",
    workshop: "Brackwater Metalworks",
    material: "Copper, brass handle",
    capacityLitres: 1.6,
    unitCost: 38.4,
    freight: 4.2,
    rrp: 95,
    leadTimeWeeks: 9,
    minimumOrder: 150,
    qc: "Passed",
    qcNote: "3 of 3 units passed. Lid seats evenly; handle rivets within tolerance.",
    received: "2026-09-02",
    photos: [
      { fileId: "ostrey-smp-0412-front.webp" },
      { fileId: "ostrey-smp-0412-back.webp" },
    ],
  },
  {
    id: "rec_smp_0419",
    code: "SMP-0419",
    title: "Steel stovetop kettle",
    workshop: "Kellet & Moor",
    material: "Stainless steel, ebonised ash handle",
    capacityLitres: 1.5,
    unitCost: 29.1,
    freight: 2.6,
    rrp: 78,
    leadTimeWeeks: 6,
    minimumOrder: 300,
    qc: "Needs fix",
    qcNote: "Handle loose on 1 of 3 units; rivet torque below spec. Resample requested.",
    received: "2026-09-11",
    photos: [
      { fileId: "ostrey-smp-0419-front.webp" },
      { fileId: "ostrey-smp-0419-back.webp" },
    ],
  },
];

/** Margin at RRP excluding VAT, in percentage points to one decimal. */
export function marginPercent(sample: SampleRecord): number {
  const exVat = sample.rrp / 1.2;
  return Math.round(((exVat - (sample.unitCost + sample.freight)) / exVat) * 1000) / 10;
}

/** Margin per unit at RRP excluding VAT, in pounds. */
export function marginPerUnit(sample: SampleRecord): number {
  return sample.rrp / 1.2 - (sample.unitCost + sample.freight);
}

export function landedCost(sample: SampleRecord): number {
  return Math.round((sample.unitCost + sample.freight) * 100) / 100;
}

export const QC_OPTIONS = {
  Passed: { value: "passed", label: "Passed", color: "green" },
  "Needs fix": { value: "needs_fix", label: "Needs fix", color: "amber" },
} as const satisfies Record<SampleRecord["qc"], { value: string; label: string; color: "green" | "amber" }>;

function field(
  key: string,
  label: string,
  type: WorkspaceField["type"],
  config: NonNullable<WorkspaceField["config"]> = {},
): WorkspaceField {
  // A workflow fills every field (costs from Xero, QC from the reports in
  // Google Drive), so the fields are workflow-owned: the sheet prints their
  // values rather than editors.
  return { id: `fld_${key}`, key, label, type, required: false, status: "active", config: { valueOwner: "workflow", ...config } };
}

/** The Samples data set's schema, in the order the sheet lists it. */
export const SAMPLE_FIELDS: WorkspaceField[] = [
  field("name", "Name", "text", { primary: true }),
  field("sample_id", "Sample ID", "text"),
  field("workshop", "Workshop", "text"),
  field("material", "Material", "text"),
  field("unit_cost", "Unit cost", "currency"),
  field("freight", "Freight", "currency"),
  field("landed_cost", "Landed cost", "currency"),
  field("rrp", "RRP", "currency"),
  field("margin", "Margin at RRP", "percent"),
  field("lead_time", "Lead time (weeks)", "number"),
  field("minimum_order", "Minimum order", "number"),
  field("received", "Received", "date"),
  field("qc_status", "QC status", "select", { options: [QC_OPTIONS.Passed, QC_OPTIONS["Needs fix"]] }),
  field("capacity", "Capacity (L)", "number"),
  field("qc_note", "QC note", "text", { display: "multiline" }),
  field("photo_front", "Front photograph", "file"),
  field("photo_back", "Back photograph", "file"),
];

/** A record's values, keyed by field. File fields hold the attached file's id. */
export function sampleValues(sample: SampleRecord): Record<string, unknown> {
  return {
    name: sample.title,
    sample_id: sample.code,
    workshop: sample.workshop,
    material: sample.material,
    unit_cost: sample.unitCost,
    freight: sample.freight,
    landed_cost: landedCost(sample),
    rrp: sample.rrp,
    margin: marginPercent(sample),
    lead_time: sample.leadTimeWeeks,
    minimum_order: sample.minimumOrder,
    received: sample.received,
    qc_status: QC_OPTIONS[sample.qc].value,
    capacity: sample.capacityLitres,
    qc_note: sample.qcNote,
    photo_front: sample.photos[0]!.fileId,
    photo_back: sample.photos[1]!.fileId,
  };
}

/**
 * Each field as the record sheet prints it: what RecordFieldValue renders
 * for the value (formatFieldValue's text, or a select's option badge).
 * They are written out because the formatter reads @mlx/contracts at
 * runtime, which the marketing bundle carries as types only;
 * fixture.test.ts checks every one against the application's formatter.
 */
export function sampleDisplays(sample: SampleRecord): Record<string, string> {
  const [copper] = SAMPLES;
  const isCopper = sample.id === copper!.id;
  return {
    name: sample.title,
    sample_id: sample.code,
    workshop: sample.workshop,
    material: sample.material,
    unit_cost: isCopper ? "£38.40" : "£29.10",
    freight: isCopper ? "£4.20" : "£2.60",
    landed_cost: isCopper ? "£42.60" : "£31.70",
    rrp: isCopper ? "£95.00" : "£78.00",
    margin: isCopper ? "46.2%" : "51.2%",
    lead_time: String(sample.leadTimeWeeks),
    minimum_order: String(sample.minimumOrder),
    received: isCopper ? "2 Sep 2026" : "11 Sep 2026",
    qc_status: sample.qc,
    capacity: String(sample.capacityLitres),
    qc_note: sample.qcNote,
  };
}

/** A select field's value as its option, for RecordOptionLabel. */
export function sampleOption(sample: SampleRecord) {
  return QC_OPTIONS[sample.qc];
}

/** The attached photographs' file titles, as uploaded by the workshops. */
export function photoFileName(fileId: string): string {
  return fileId.replace(/^ostrey-/, "").replace(/\.webp$/, ".jpg").replace(/^smp-(\d+)/, "SMP-$1");
}

/**
 * The sheet's photographs, built as RecordPhotographs builds them from the
 * record's file fields: one per image field, labelled with the field.
 */
export function samplePhotographs(sample: SampleRecord): RecordImage[] {
  const values = sampleValues(sample);
  return SAMPLE_FIELDS.filter((candidate) => candidate.type === "file").map((fileField) => {
    const fileId = values[fileField.key] as string;
    return { id: `${fileField.key}:${fileId}`, fileId, label: fileField.label, fileName: photoFileName(fileId) };
  });
}

/** Where the product links a record: its channel, with the record open. */
export function recordHref(record: Pick<SampleRecord, "id">): string {
  return `/workspace/channels/${CHANNEL_ID}?record=${record.id}`;
}

/* ------------------------------------------------------------------ */
/*  The turn                                                            */
/* ------------------------------------------------------------------ */

export const QUESTION =
  "Compare the two kettle samples for the autumn range. Which one should we order?";

const [COPPER, STEEL] = SAMPLES as [SampleRecord, SampleRecord];

/**
 * The answer as the run wrote it, with record links in the product's own
 * workspace form. It is short enough that the question, the answer and its
 * provenance fit one frame at 1:1; the samples' photographs are one click
 * away, on the records.
 */
/** The records the answer links, in reading order: the sheet's previous and next. */
export const ANSWER_RECORD_IDS = ["rec_smp_0412", "rec_smp_0419"] as const;

export const ANSWER_MARKDOWN = `Brackwater's copper kettle ([SMP-0412](${recordHref(COPPER)})) is the one you can order now. It passed QC on all three units and earns **£36.57** a unit at its £95 RRP. Kellet & Moor's steel kettle ([SMP-0419](${recordHref(STEEL)})) lands £10.90 cheaper and arrives three weeks sooner, but QC found a loose handle on one of three units.

| | SMP-0412 · Brackwater | SMP-0419 · Kellet & Moor |
| --- | --- | --- |
| Landed cost | £42.60 | £31.70 |
| Margin per unit, ex VAT | £36.57 | £33.30 |
| Lead time | 9 weeks | 6 weeks |
| Minimum order | 150 | 300 |
| QC | Passed, 3 of 3 | Needs fix, 1 of 3 failed |`;
